/**
 * The command registry — one definition per command, checked total at compile
 * time.
 *
 * `COMMAND_DEFINITIONS` is typed as `{ [K in CommandType]: CommandDefinition<K> }`.
 * That mapped type is the exhaustiveness check the milestone asks for: add a
 * key to `CommandPayloadMap` without adding its definition and `tsc` reports a
 * missing property; register a definition under the wrong key and the
 * `type: K` field fails to match. There is no runtime assertion doing this job
 * and no way to ship a half-registered command.
 */

import type { CommandDefinition } from './definition';
import * as batch from './definitions/batch';
import * as blend from './definitions/blend';
import * as customIngredients from './definitions/custom-ingredient';
import * as ingredients from './definitions/ingredients';
import * as inventory from './definitions/inventory';
import * as journal from './definitions/journal';
import * as miscProfile from './definitions/misc-profile';
import * as recipe from './definitions/recipe';
import * as recipeSnapshots from './definitions/recipe-snapshots';
import * as scheduleProfile from './definitions/schedule-profile';
import * as schedules from './definitions/schedules';
import * as sharedRecipes from './definitions/shared-recipes';
import * as water from './definitions/water';
import * as waterProfiles from './definitions/water-profiles';
import * as workspace from './definitions/workspace';
import type { CommandType } from './types';

/** Every command definition, keyed by command name. */
export const COMMAND_DEFINITIONS: { readonly [K in CommandType]: CommandDefinition<K> } = {
  'recipe.create': recipe.recipeCreate,
  'recipe.restore': recipe.recipeRestore,
  'recipe.delete': recipe.recipeDelete,
  'recipe.rename': recipe.recipeRename,
  'recipe.setStyle': recipe.recipeSetStyle,
  'recipe.setTargetOg': recipe.recipeSetTargetOg,
  'recipe.setTargetSrm': recipe.recipeSetTargetSrm,
  'recipe.setArchived': recipe.recipeSetArchived,
  'recipe.setBatchSize': recipe.recipeSetBatchSize,
  'recipe.setBoilTime': recipe.recipeSetBoilTime,
  'recipe.setEquipment': recipe.recipeSetEquipment,
  'recipe.setEfficiency': recipe.recipeSetEfficiency,
  'recipe.setCarbonation': recipe.recipeSetCarbonation,
  'recipe.setPackaging': recipe.recipeSetPackaging,
  'recipe.setNotes': recipe.recipeSetNotes,
  'recipe.setTags': recipe.recipeSetTags,
  'recipe.scale': recipe.recipeScale,
  'recipe.revertTo': recipe.recipeRevertTo,
  'recipeSnapshot.create': recipeSnapshots.recipeSnapshotCreate,
  'recipeSnapshot.restore': recipeSnapshots.recipeSnapshotRestore,
  'recipeSnapshot.delete': recipeSnapshots.recipeSnapshotDelete,
  'recipeSnapshot.rename': recipeSnapshots.recipeSnapshotRename,
  'recipe.addFermentable': ingredients.recipeAddFermentable,
  'recipe.updateFermentable': ingredients.recipeUpdateFermentable,
  'recipe.removeFermentable': ingredients.recipeRemoveFermentable,
  'recipe.reorderFermentables': ingredients.recipeReorderFermentables,
  'recipe.addHop': ingredients.recipeAddHop,
  'recipe.updateHop': ingredients.recipeUpdateHop,
  'recipe.removeHop': ingredients.recipeRemoveHop,
  'recipe.reorderHops': ingredients.recipeReorderHops,
  'recipe.addYeast': ingredients.recipeAddYeast,
  'recipe.updateYeast': ingredients.recipeUpdateYeast,
  'recipe.removeYeast': ingredients.recipeRemoveYeast,
  'recipe.reorderYeasts': ingredients.recipeReorderYeasts,
  'recipe.addMisc': ingredients.recipeAddMisc,
  'recipe.updateMisc': ingredients.recipeUpdateMisc,
  'recipe.removeMisc': ingredients.recipeRemoveMisc,
  'recipe.reorderMiscs': ingredients.recipeReorderMiscs,
  'recipe.addMashStep': schedules.recipeAddMashStep,
  'recipe.updateMashStep': schedules.recipeUpdateMashStep,
  'recipe.removeMashStep': schedules.recipeRemoveMashStep,
  'recipe.reorderMashSteps': schedules.recipeReorderMashSteps,
  'recipe.addFermentationStep': schedules.recipeAddFermentationStep,
  'recipe.updateFermentationStep': schedules.recipeUpdateFermentationStep,
  'recipe.removeFermentationStep': schedules.recipeRemoveFermentationStep,
  'recipe.reorderFermentationSteps': schedules.recipeReorderFermentationSteps,
  'water.setSourceProfile': water.waterSetSourceProfile,
  'water.setDilution': water.waterSetDilution,
  'water.setSaltAdditions': water.waterSetSaltAdditions,
  'water.setAcidAdditions': water.waterSetAcidAdditions,
  'water.setTargetProfile': water.waterSetTargetProfile,
  'water.setTargetRule': water.waterSetTargetRule,
  'water.setTargetPh': water.waterSetTargetPh,
  'water.setKettleTargetPh': water.waterSetKettleTargetPh,
  'water.clear': water.waterClear,
  'equipment.create': workspace.equipmentCreate,
  'equipment.update': workspace.equipmentUpdate,
  'equipment.delete': workspace.equipmentDelete,
  'equipment.restore': workspace.equipmentRestore,
  'mashProfile.create': scheduleProfile.mashProfileCreate,
  'mashProfile.update': scheduleProfile.mashProfileUpdate,
  'mashProfile.delete': scheduleProfile.mashProfileDelete,
  'mashProfile.restore': scheduleProfile.mashProfileRestore,
  'fermentationProfile.create': scheduleProfile.fermentationProfileCreate,
  'fermentationProfile.update': scheduleProfile.fermentationProfileUpdate,
  'fermentationProfile.delete': scheduleProfile.fermentationProfileDelete,
  'fermentationProfile.restore': scheduleProfile.fermentationProfileRestore,
  'miscProfile.create': miscProfile.miscProfileCreate,
  'miscProfile.update': miscProfile.miscProfileUpdate,
  'miscProfile.delete': miscProfile.miscProfileDelete,
  'miscProfile.restore': miscProfile.miscProfileRestore,
  'waterProfile.create': waterProfiles.waterProfileCreate,
  'waterProfile.update': waterProfiles.waterProfileUpdate,
  'waterProfile.delete': waterProfiles.waterProfileDelete,
  'waterProfile.restore': waterProfiles.waterProfileRestore,
  'batch.create': batch.batchCreate,
  'batch.restore': batch.batchRestore,
  'batch.resnapshot': batch.batchResnapshot,
  'batch.delete': batch.batchDelete,
  'batch.rename': batch.batchRename,
  'batch.setStatus': batch.batchSetStatus,
  'batch.setBrewedAt': batch.batchSetBrewedAt,
  'batch.setPackagedOn': batch.batchSetPackagedOn,
  'batch.updateMeasurements': batch.batchUpdateMeasurements,
  'batch.addFermentationReading': batch.batchAddFermentationReading,
  'batch.removeFermentationReading': batch.batchRemoveFermentationReading,
  'batch.setStageNotes': batch.batchSetStageNotes,
  'batch.toggleStep': batch.batchToggleStep,
  'batch.setStepTimer': batch.batchSetStepTimer,
  'batch.setInventoryDeducted': batch.batchSetInventoryDeducted,
  'batch.setPitchedYeast': batch.batchSetPitchedYeast,
  'batch.setPackaging': batch.batchSetPackaging,
  'batch.setYeastStarter': batch.batchSetYeastStarter,
  'batch.setYeastPitchedAt': batch.batchSetYeastPitchedAt,
  'batch.addCellarTask': batch.batchAddCellarTask,
  'batch.updateCellarTask': batch.batchUpdateCellarTask,
  'batch.removeCellarTask': batch.batchRemoveCellarTask,
  'batch.addTasting': batch.batchAddTasting,
  'batch.updateTasting': batch.batchUpdateTasting,
  'batch.removeTasting': batch.batchRemoveTasting,
  'batch.addPhoto': batch.batchAddPhoto,
  'batch.setPhotoCaption': batch.batchSetPhotoCaption,
  'batch.removePhoto': batch.batchRemovePhoto,
  'blend.create': blend.blendCreate,
  'blend.update': blend.blendUpdate,
  'blend.setStage': blend.blendSetStage,
  'blend.delete': blend.blendDelete,
  'blend.restore': blend.blendRestore,
  'blend.addCellarTask': blend.blendAddCellarTask,
  'blend.updateCellarTask': blend.blendUpdateCellarTask,
  'blend.removeCellarTask': blend.blendRemoveCellarTask,
  'journal.create': journal.journalCreate,
  'journal.update': journal.journalUpdate,
  'journal.delete': journal.journalDelete,
  'journal.restore': journal.journalRestore,
  'journal.addBlock': journal.journalAddBlock,
  'journal.setBlock': journal.journalSetBlock,
  'journal.moveBlock': journal.journalMoveBlock,
  'journal.removeBlock': journal.journalRemoveBlock,
  'inventory.add': inventory.inventoryAdd,
  'inventory.update': inventory.inventoryUpdate,
  'inventory.remove': inventory.inventoryRemove,
  'inventory.restore': inventory.inventoryRestore,
  'inventory.adjustQuantity': inventory.inventoryAdjustQuantity,
  'customIngredient.add': customIngredients.customIngredientAdd,
  'customIngredient.update': customIngredients.customIngredientUpdate,
  'customIngredient.remove': customIngredients.customIngredientRemove,
  'customIngredient.restore': customIngredients.customIngredientRestore,
  'recipe.share': sharedRecipes.recipeShare,
  'sharedRecipe.join': sharedRecipes.sharedRecipeJoin,
  'sharedRecipe.leave': sharedRecipes.sharedRecipeLeave,
  'sharedRecipe.setEquipmentOverride': sharedRecipes.sharedRecipeSetEquipmentOverride,
  'settings.update': workspace.settingsUpdate,
};

/**
 * Every command name, sorted.
 *
 * Sorted rather than declaration-ordered so that anything derived from it —
 * documentation, an AI tool schema, a test that enumerates the set — is stable
 * regardless of how the registry is edited.
 */
export const COMMAND_TYPES: readonly CommandType[] = (
  Object.keys(COMMAND_DEFINITIONS) as CommandType[]
).sort();

/** Whether a string names a registered command. */
export function isCommandType(value: string): value is CommandType {
  return Object.hasOwn(COMMAND_DEFINITIONS, value);
}
